import React, { useCallback, useMemo } from 'react';
import { ThreeEvent } from '@react-three/fiber';

import { CalculatedLine, Component } from '@paragon/contract-library';
import { RaycastOrder } from '@paragon/three-shared';

import { useAppDispatch } from '../../components/hooks';
import {
  getReferenceLineEditablePointIntersections,
  ReferenceLineIntersectionAndKey,
} from '../../library/relativeGeometry/getReferenceLineEditableIntersectionPoints';
import { selectEditableReferenceLineIntersection } from '../../store/actions/appActions';
import { ReferenceLineIntersection } from '../../store/slices/applicationSlice';
import { SnapVisual } from '../dimension/SnapVisual';
import { useMousePositionInScene } from '../hooks/useMousePositionInScene';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { raycastReferenceLineContainer } from '../util/raycastReferenceLineContainer';
import { getClosestIntersection } from './getClosestIntersection';

interface Props {
  readonly component: Component;
  readonly selectedLine: CalculatedLine;
}

export function ReferenceLineEditableIntersections({ component, selectedLine }: Props) {
  const dispatch = useAppDispatch();
  const mousePosition = useMousePositionInScene();

  const editablePointIntersectionsAndKeys = useMemo(
    (): readonly ReferenceLineIntersectionAndKey[] =>
      getReferenceLineEditablePointIntersections(selectedLine, component),
    [component, selectedLine],
  );

  const intersections = useMemo(
    (): readonly ReferenceLineIntersection[] =>
      editablePointIntersectionsAndKeys.map(({ intersection }) => intersection),
    [editablePointIntersectionsAndKeys],
  );

  const hoveredIntersection = mousePosition ? getClosestIntersection(mousePosition, intersections) : null;

  const onClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (!threeJsDom.shouldDispatchClickEvent()) return;

      const closestIntersection = getClosestIntersection(event.point, intersections);
      if (!closestIntersection) return;

      const intersectionAndKeys = editablePointIntersectionsAndKeys.find(
        (intersectionAndKey) => intersectionAndKey.intersection === closestIntersection,
      );
      if (!intersectionAndKeys) return;

      event.stopPropagation();
      dispatch(selectEditableReferenceLineIntersection(intersectionAndKeys));
    },
    [dispatch, editablePointIntersectionsAndKeys, intersections],
  );

  return (
    <group
      onClick={onClick}
      raycast={raycastReferenceLineContainer}
      userData-raycastOrder={RaycastOrder.ReferenceLineIntersections}
    >
      {editablePointIntersectionsAndKeys.map((intersectionAndKey, index) => (
        <SnapVisual
          key={index}
          snapType="Vertex"
          position={[intersectionAndKey.intersection.point.x, intersectionAndKey.intersection.point.y, 0]}
          size={0.5}
          isSelected={intersectionAndKey.intersection === hoveredIntersection}
        />
      ))}
    </group>
  );
}
